//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { ReactChild } from 'react';
import { useSelector } from 'react-redux';

import { ContentLibraryContentGroup } from '@apis/content-library/contentLibraryApiModel';
import { IUserInfo } from '@apis/users/usersApiModel';
import { BodyText } from '@components/atoms/text/Text';
import { useAllUsersQuery } from '@content-drawer/filters/useAllUsersQuery';
import { getCurrentUser } from '@redux/selectors/getSessionInfo';

export interface MediaSource {
  contentGroup?: ContentLibraryContentGroup;
  id: string;
  label: string;
  tooltip?: ReactChild;
}

export interface MediaSourceSection {
  label: string;
  sources: MediaSource[];
}

export const ALL_MEDIA_SOURCE: MediaSource = {
  contentGroup: ContentLibraryContentGroup.ALL,
  id: 'all_media',
  label: 'All Media',
  tooltip: (
    <BodyText>
      Items that you and your team members have:
      <ul>
        <li>Uploaded</li>
        <li>Added to Media Library</li>
        <li>Labeled</li>
        <li>Product tagged</li>
        <li>Requested rights</li>
        <li>Distributed</li>
      </ul>
    </BodyText>
  ),
};

export const mediaSection: MediaSourceSection = {
  label: 'Media',
  sources: [
    ALL_MEDIA_SOURCE,
    {
      contentGroup: ContentLibraryContentGroup.TEAM,
      id: 'team_media',
      label: 'Team Media',
      tooltip: <>Items uploaded and added by you and your team members</>,
    },
  ],
};

export const teamSection: MediaSourceSection = {
  label: 'Team members',
  sources: [],
};

export const useMediaSourcesSections = (filteredUsers?: IUserInfo[]) => {
  const { data, isPending } = useAllUsersQuery();
  const user = useSelector(getCurrentUser);

  const myUser: IUserInfo = {
    firstName: user.firstName,
    id: user.id,
    lastName: `${user.lastName} (you)`,
  };

  const allUsers = [
    myUser,
    ...(data ?? []).filter((user) => user.id !== myUser.id),
  ];

  const userSources = (filteredUsers ?? allUsers).map((user) => ({
    id: `${user.id}`,
    label: `${user.firstName} ${user.lastName}`,
  }));

  return {
    allUsers,
    isPending,
    sections: [
      mediaSection,
      {
        ...teamSection,
        sources: userSources,
      },
    ],
  };
};
